import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import FileSaver from "file-saver";
import { ReplaySubject, Observable, Subject, map } from "rxjs";

import { environment } from "@environments/environment";

import { Autorizacion } from "@shared/Entities/Autorizaciones/autorizacion.entity";
import { RazonAnulacion } from "@shared/Entities/Autorizaciones/razon.entity";
import { UserRoleName } from "@shared/Entities/Usuario/usuario-role-id.enum";
import { Usuario } from "@shared/Entities/Usuario/usuario.entity";
import { AutorizacionesRepository } from "@shared/Repositories/autorizaciones.repository";
import { UsuariosRepository } from "@shared/Repositories/usuarios.repository";
import { appInsight } from "@shared/Services/appInsight.service";

import { Service } from "./Types/service.enum";

const { aplicationOdontograma } = environment;

@Injectable({ providedIn: "root" })
export class FormularioAutorizacionesService {
  private autorizacion: Autorizacion;
  private $autorizacion: ReplaySubject<Autorizacion>;
  private currentUser: Usuario;
  private $currentUser: ReplaySubject<Usuario>;

  private $razonesAnulacion: ReplaySubject<RazonAnulacion[]>;

  private $prestacionesAprobadas: ReplaySubject<any> = new ReplaySubject<any>();
  private $prestacionesRechazadas: ReplaySubject<any> = new ReplaySubject<any>();

  private requiredFields = {
    codigoRemitente: false,
    codigoTratante: false,
    telefono: false,
    email: false,
    codigoDiagnostico: false,
    observacion: false,
  };
  private $requiredFields: ReplaySubject<any>;

  constructor(
    private userRepository: UsuariosRepository,
    private autorizacionesRepository: AutorizacionesRepository,
    private AplicationInsight: appInsight,
    private router: Router
  ) {
    this.$autorizacion = new ReplaySubject(1);
    this.$requiredFields = new ReplaySubject(1);
  }

  getCurrentUser(): Observable<Usuario> {
    if (!this.$currentUser) this.$currentUser = new ReplaySubject(1);

    if (!this.currentUser || !this.currentUser.identificacion) this.searchCurrentUser();

    return this.$currentUser;
  }

  //GETS
  getAutorizacion(): Observable<Autorizacion> {
    return this.$autorizacion;
  }
  getCamposRequeridos(): Observable<any> {
    return this.$requiredFields;
  }
  getMotivosAnulacion(): Observable<RazonAnulacion[]> {
    if (!this.$razonesAnulacion) this.$razonesAnulacion = new ReplaySubject(1);

    this.autorizacionesRepository.getMotivosAnulacion().subscribe((motivosAnulacion) => {
      this.$razonesAnulacion.next(motivosAnulacion);
    });

    return this.$razonesAnulacion;
  }
  getPrestacionesAprobadas(): Observable<any> {
    return this.$prestacionesAprobadas;
  }
  getPrestacionesRechazadas(): Observable<any> {
    return this.$prestacionesRechazadas;
  }

  newAutorizacion() {
    this.autorizacion = new Autorizacion();
    this.autorizacion.fecha = new Date();

    this.requiredFields = {
      codigoRemitente: false,
      codigoTratante: false,
      telefono: false,
      email: false,
      codigoDiagnostico: false,
      observacion: false,
    };

    this.$prestacionesAprobadas.next([]);
    this.$prestacionesRechazadas.next([]);

    this.updateAutorizacion();
    return this.autorizacion;
  }

  //FLUJO DE AUTORIZACION
  iniciarCaso(autorizacion): Observable<any> {
    const result = new Subject<any>();
    this.autorizacionesRepository
      .validarCobertura(
        this.currentUser.identificacion,
        "",
        this.currentUser.compania,
        this.currentUser.companiaSucursal,
        autorizacion.afiliado.numero,
        autorizacion.servicio.codigo
      )
      .subscribe(
        (idInteraccion) => {
          this.autorizacion = Object.assign({}, autorizacion, {
            idInteraccion: idInteraccion,
          });
          this.updateAutorizacion();

          this.setCamposRequeridos().subscribe((camposObtenidos) => {
            result.next(true);
          });
        },
        (error) => {
          result.next(error);
        }
      );
    return result;
  }
  crearCaso(autorizacion): Observable<any> {
    const result = new Subject<any>();
    const requireOdontogram = this.currentUser.usuarioRole.some(({ role }) => role.nombre === UserRoleName.NewOdontograma);

    this.autorizacionesRepository.crearCaso(this.currentUser, autorizacion).subscribe(
      (numeroCaso) => {
        this.autorizacion = Object.assign({}, autorizacion);
        this.updateAutorizacion();
        this.AplicationInsight.logEvent("Autorizaciones.CrearCasoAutorizacion", {
          Identificacion: this.currentUser.identificacion,
          Nombre: this.currentUser.nombreCompleto,
          Compania: this.currentUser.companiaNombre,
          CodigoPrestador: this.currentUser.metadata.codigoPrestador,
        });

        if (autorizacion.servicio.codigo.trim() === Service.SERVICIOS_ODONTOLOGICOS && requireOdontogram) {
          return this.autorizacionesRepository
            .startOdotograma(this.currentUser, autorizacion)
            .pipe(
              map(({ Data }) => {
                window.open(`${aplicationOdontograma}/?sesionId=${Data}&urlToReturn=${window.location.href}`, "_self");
              })
            )
            .subscribe();
        } else {
          result.next(true);
        }
      },
      (error) => {
        result.next(error);
      }
    );
    return result;
  }
  crearAutorizacion(autorizacion): Observable<any> {
    const result = new Subject<any>();

    this.autorizacionesRepository.crearAutorizacion(this.currentUser.identificacion, autorizacion).subscribe(
      (nuevaAutorizacion) => {
        this.autorizacion.numeroCaso = nuevaAutorizacion;
        this.autorizacion = Object.assign({}, autorizacion, {
          numeroCaso: nuevaAutorizacion,
        });

        this.updateAutorizacion();
        this.$getPrestacionesAprobadas();
        this.$getPrestacionesRechazadas();
        this.AplicationInsight.logEvent("Autorizaciones.CrearAutorizacion", {
          numeroAutorizacion: nuevaAutorizacion.toString(),
          Identificacion: this.currentUser.identificacion,
          Nombre: this.currentUser.nombreCompleto,
          Compania: this.currentUser.companiaNombre,
          CodigoPrestador: this.currentUser.metadata.codigoPrestador,
        });
        result.next(true);
      },
      (error) => {
        result.next(error);
      }
    );
    return result;
  }

  //CANCELACION Y ANULACION
  cancelCase(): Observable<any> {
    const result = new Subject<any>();

    this.autorizacionesRepository.cancelarCaso(this.currentUser.identificacion, this.autorizacion).subscribe(
      (casoCancelado) => {
        result.next(true);
      },
      (error) => {
        result.next(error);
      }
    );

    return result;
  }

  //REPORTES
  downloadReporte(tipoReporte) {
    const mediaType = "application/pdf";
    this.autorizacionesRepository.obtenerReporteARS(this.autorizacion.numeroCaso, tipoReporte).subscribe((response) => {
      const blob = new Blob([response], { type: mediaType });
      FileSaver.saveAs(blob, "Autorizacion.pdf");
    });
  }

  private searchCurrentUser() {
    this.userRepository.getInfoClienteActual().subscribe((currentUser) => {
      this.currentUser = currentUser;
      this.$currentUser.next(this.currentUser);
    });
  }
  private updateAutorizacion() {
    this.$autorizacion.next(this.autorizacion);
  }
  private setCamposRequeridos(): Observable<boolean> {
    const result = new Subject<boolean>();
    this.autorizacionesRepository
      .getCamposRequeridos(this.currentUser.identificacion, this.autorizacion.idInteraccion)
      .subscribe((camposRequeridos) => {
        this.parseCamposRequeridos(camposRequeridos);
        this.$requiredFields.next(this.requiredFields);
        result.next(true);
      });

    return result;
  }
  private parseCamposRequeridos(camposRequeridos) {
    this.requiredFields = {
      codigoRemitente: false,
      codigoTratante: false,
      telefono: false,
      email: false,
      codigoDiagnostico: false,
      observacion: false,
    };

    camposRequeridos.forEach((campoRequerido) => {
      switch (campoRequerido) {
        case "telefono": {
          this.requiredFields.telefono = true;
          break;
        }
        case "codigoRemitente": {
          this.requiredFields.codigoRemitente = true;
          break;
        }
        case "idDoctor": {
          this.requiredFields.codigoTratante = true;
          break;
        }
        case "email": {
          this.requiredFields.email = true;
          break;
        }
        case "codigoDiagnostico": {
          this.requiredFields.codigoDiagnostico = true;
          break;
        }
        case "observacion": {
          this.requiredFields.observacion = true;
          break;
        }
      }
    });
  }
  private $getPrestacionesAprobadas() {
    this.autorizacionesRepository
      .getPrestacionesAprobadas(this.currentUser.identificacion, this.autorizacion)
      .subscribe((prestacionesAprobadas) => {
        this.$prestacionesAprobadas.next(prestacionesAprobadas);
      });
  }
  private $getPrestacionesRechazadas() {
    this.autorizacionesRepository
      .getPrestacionesNegadas(this.currentUser.identificacion, this.autorizacion)
      .subscribe((prestacionesNegadas) => {
        this.$prestacionesRechazadas.next(prestacionesNegadas);
      });
  }
}
